
"use client";

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import React from 'react';
import Delta from 'quill-delta';




import { useNotes } from "../contexts/NotesContext";
import { useCommunication } from '@/lib/CommunicationWrapper';
import { ContentBottomGradient } from './ContentBottomGradient';
import { normalizeNative } from '../lib/util';

class NotesErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error?: any }>
{
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError(error: any) {
        return { hasError: true, error };
    }
    componentDidCatch(error: any, info: any) {
        console.error('NoteEditorTab error:', error, info);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="p-3 text-xs text-red-600 bg-red-50 rounded border border-red-200">
                    <div className="font-medium mb-1">Something went wrong loading the editor.</div>
                    <button
                        className="mt-1 px-2 py-1 rounded bg-black text-white"
                        onClick={() => this.setState({ hasError: false, error: undefined })}
                    >
                        Try again
                    </button>
                </div>
            );
        }
        return this.props.children as any;
    }
}

export const NoteEditorTab = () => {

    const { activeTab, quillEditor, setQuillEditor, content, noteId, contentInit, settings, getNoteContent, isSaving, setIsSaving } = useNotes();

    const editorRef = useRef<HTMLDivElement>(null);
    const timeout = useRef<any>(null);
    const accumulatedDeltas = useRef<Delta>(new Delta());
    const [currentSelection, setCurrentSelection] = useState<{ index: number; length: number } | null>(null);

    const { authenticatedClient } = useCommunication();

    const sendDeltaToServer = async () => {
        if (!accumulatedDeltas.current?.ops?.length || isSaving) return;
        
        setIsSaving(true);
        const deltaToSave = new Delta(accumulatedDeltas.current);
        accumulatedDeltas.current = new Delta(); // Reset immediately
        
        const client = await authenticatedClient();
        if (!client) {
            console.error('Error creating Supabase client:');
            setIsSaving(false);
            return;
        }

        try {
            console.log("🚀 SENDING DELTA TO SERVER:", deltaToSave);
            console.log("🚀 DELTA OPS COUNT:", deltaToSave.ops?.length);

            const response = await client.functions.invoke('modify-note', {
                body: { noteId, delta: deltaToSave }
            });
            
            if (response.error) {
                // Re-compose failed delta
                accumulatedDeltas.current = deltaToSave.compose(accumulatedDeltas.current);
                throw new Error('Save failed');
            }
        } catch (error) {
            console.error('Error saving note:', error);
            // Delta already re-composed above
        } finally {
            setIsSaving(false);
        }
    }

    useEffect(() => {
        if (quillEditor && noteId) {
            // Reset contentInit flag and accumulated deltas before loading new content
            contentInit.current = false;
            accumulatedDeltas.current = new Delta();
            console.log("🔄 SWITCHING TO NOTE:", noteId, "- Reset deltas and contentInit");
            getNoteContent();
        }
    }, [quillEditor, noteId]);


    useEffect(() => {

        if (!editorRef.current) return;

        // Dynamic import to prevent SSR issues
        const initQuill = async () => {
            const { default: Quill } = await import('quill');
            await import('quill/dist/quill.bubble.css');
            
            const root = editorRef.current;
            if (!root) return;
            const quill = new Quill(root, {
            bounds: root,
            placeholder: "",
            modules: {
                toolbar: false,
            }
        });

        // Debug: Add keyboard event listeners to diagnose question mark issue
        quill.root.addEventListener('keydown', (e) => {
            console.log('🔍 Keydown:', { key: e.key, code: e.code, keyCode: e.keyCode, which: e.which });
            if (e.key === '?' || e.code === 'Slash') {
                console.log('🔍 Question mark key detected in keydown!');

                // Force insert question mark if it's being blocked
                if (e.key === '?' && e.shiftKey) {
                    e.preventDefault();
                    const selection = quill.getSelection();
                    if (selection) {
                        quill.insertText(selection.index, '?');
                        quill.setSelection(selection.index + 1, 0);
                    }
                }
            }
        });

        quill.root.addEventListener('keydown', (e) => {
            const selection = quill.getSelection();
            if (!selection) return;

            const [line] = quill.getLine(selection.index);
            const format = quill.getFormat(selection.index);
            
            if (e.key === 'Backspace') {
                console.log("🔍 Backspace - Selection:", selection);
                console.log("🔍 Line:", line, line?.domNode?.textContent?.length!, "Format:", format);

                const shouldDeleteLine = (line && !line?.domNode?.textContent)
                || (line && line.domNode?.textContent && line.domNode?.textContent?.length <= 1);

                if (shouldDeleteLine) {
                    console.log('🔍 Deleting empty line');
                    const lineStart = quill.getIndex(line);
                    quill.deleteText(lineStart, line.length());
                    e.preventDefault();
                    return;
                }
            }

            if (e.key === 'Enter') {
                console.log("🔍 Enter - Format:", format, "Line content:", line?.domNode?.textContent);
                
                // If we're in a list and the current line is empty, create a new regular line
                if (format.list && line && (!line.domNode?.textContent || line.domNode?.textContent?.trim() === '')) {
                    console.log('🔍 Creating new line after empty bullet');
                    // Remove list formatting and let enter create a new line
                    quill.format('list', false);
                    // Don't prevent default - let enter create the new line
                    return;
                }
            }
        });

        // ✅ SIMPLE SELECTION TRACKING - Re-enabled to keep toolbar in sync
        quill.on('selection-change', (range: { index: number; length: number; } | null) => {
            console.log("✅ SELECTION CHANGED:", range);
            setCurrentSelection(range);

            // Trigger custom selection change event for toolbar
            const selectionEvent = new CustomEvent('quill-selection-change', {
                detail: { range, quill },
                bubbles: true
            });
            editorRef.current?.dispatchEvent(selectionEvent);
        });

        // Add text-change handler - Custom implementation for shadow DOM
        quill.on('text-change', (delta: any, oldDelta: any, source: any) => {
            console.log("✅ TEXT CHANGED:", delta, "Source:", source);

            // Skip processing if this is the initial content load
            if (contentInit.current === false) {
                quill.setSelection(quill.getLength(), 0);
                contentInit.current = true;
                return;
            }

            // Get selection before processing
            const selection = quill.getSelection();
            const startIndex = selection ? selection.index : 0;

            // Check if this is a list formatting change (like pressing enter twice)
            let isListFormatChange = false;
            for (const op of delta.ops) {
                if (op.insert && typeof op.insert === 'object' && op.insert.list) {
                    isListFormatChange = true;
                    break;
                }
                if (op.attributes && (op.attributes.list !== undefined)) {
                    isListFormatChange = true;
                    break;
                }
            }

            // Calculate cursor position after text changes (required for shadow DOM)
            let newCursorPosition = startIndex; // Default to original position
            let oldIndex = 0; // Position in original document
            let newIndex = 0; // Position in new document
            let cursorFound = false;

            // Process delta operations to determine new cursor position
            for (const op of delta.ops) {
                if (op.retain) {
                    const retainLength = op.retain;
                    
                    // If cursor is within this retained section
                    if (!cursorFound && oldIndex <= startIndex && startIndex < oldIndex + retainLength) {
                        const offsetInRetain = startIndex - oldIndex;
                        newCursorPosition = newIndex + offsetInRetain;
                        cursorFound = true;
                    }
                    
                    oldIndex += retainLength;
                    newIndex += retainLength;
                    
                } else if (op.insert) {
                    const insertLength = typeof op.insert === 'string' ? op.insert.length : 1;
                    
                    // If cursor is at the insertion point, place it after the insert
                    if (!cursorFound && oldIndex === startIndex) {
                        newCursorPosition = newIndex + insertLength;
                        cursorFound = true;
                    }
                    
                    newIndex += insertLength;
                    // oldIndex doesn't change for inserts
                    
                } else if (op.delete) {
                    const deleteLength = op.delete;
                    
                    // If cursor is within deleted content, place it at deletion point
                    if (!cursorFound && oldIndex <= startIndex && startIndex < oldIndex + deleteLength) {
                        newCursorPosition = newIndex;
                        cursorFound = true;
                    }
                    // If cursor is after deleted content, adjust position
                    else if (!cursorFound && startIndex >= oldIndex + deleteLength) {
                        // Cursor will move back by the deletion amount (handled in next iteration)
                    }
                    
                    oldIndex += deleteLength;
                    // newIndex doesn't change for deletes
                }
            }
            
            // If cursor was at the very end, make sure it stays at the end
            if (!cursorFound && startIndex >= oldIndex) {
                newCursorPosition = newIndex;
            }

                        // Set cursor position after text change (essential for shadow DOM)
            // Only adjust cursor for user typing; avoid overriding toolbar-applied selections
            if (source === 'user') {
                // Use longer timeout for list formatting changes to let Quill finish processing
                const cursorTimeout = isListFormatChange ? 50 : 0;
                setTimeout(() => {
                    try {
                        // Ensure cursor position is within document bounds
                        const documentLength = quill.getLength();
                        const safePosition = Math.min(Math.max(0, newCursorPosition), documentLength - 1);
                        quill.setSelection(safePosition, 0);
                        
                        // For list formatting changes, ensure we trigger a selection update
                        if (isListFormatChange) {
                            const currentSelection = quill.getSelection();
                            if (currentSelection) {
                                // Dispatch custom selection event for toolbar
                                const selectionEvent = new CustomEvent('quill-selection-change', {
                                    detail: { range: currentSelection, quill },
                                    bubbles: true
                                });
                                editorRef.current?.dispatchEvent(selectionEvent);
                            }
                        }
                    } catch (error) {
                        console.warn("Could not set cursor position:", error);
                    }
                }, cursorTimeout);
            }

            // Handle content saving
            // Accept both 'user' (typing) and 'api' (formatting/toolbar) sources
            if (source === 'user' || source === 'api') {
                // Accumulate deltas for efficient saving
                accumulatedDeltas.current = accumulatedDeltas.current.compose(delta);
                console.log("🔍 ACCUMULATED DELTA:", accumulatedDeltas.current);
                console.log("🔍 NEW DELTA:", delta, "Source:", source);
                
                // Update content reference immediately for local state
                content.current[noteId] = quill.getContents();
                
                // Debounce server saves to avoid excessive API calls
                if (timeout.current) {
                    clearTimeout(timeout.current);
                }
                
                timeout.current = setTimeout(async () => {
                    timeout.current = null;
                    await sendDeltaToServer();
                }, 2000);
            }
        });


        /*
        document.addEventListener("selectionchange", (...args) => {

            quill.selection.update()
        });

        quill.selection.getNativeRange = () => {

            const dom = quill.root.getRootNode();
            const selection = (dom as any).getSelection();
            const range = normalizeNative(selection);

            return range;
        };
        */

        // Load note content


        // Focus the editor
        setTimeout(() => {
            quill.focus();
        }, 100);

        setQuillEditor(quill);

        return () => {
            if (quill) {
                //quill.off('selection-change');
                //quill.off('text-change');
                setQuillEditor(null);
            }

            if (timeout.current) {
                clearTimeout(timeout.current);
                timeout.current = null;
            }
        };
        };
        
        initQuill();
    }, [noteId, activeTab]);



    useEffect(() => {

        if (!quillEditor) return;

        quillEditor.selection.getNativeRange = () => {

            const dom = quillEditor.root.getRootNode();
            const selection = dom.getSelection();
            const range = normalizeNative(selection);

            return range;
        };

        document.addEventListener("selectionchange", (...args) => {

            // Update selection and some other properties

            quillEditor.selection.update()
        });
    }, [quillEditor])

    if (activeTab !== "notes") return null;

    return (
        <NotesErrorBoundary>
            <div className="flex-1 min-h-0 relative">
            <style>
                {`
                .ql-editor h1 {
                    font-size: 24px;
                    font-weight: 600;
                }
                .ql-editor h2 {
                    font-size: 20px;
                    font-weight: 600;
                }
                .ql-editor h3 {
                    font-size: 16px;
                    font-weight: 600;
                }
                .ql-editor p {
                    font-size: 14px;
                }
                .ql-editor {
                    font-family: var(--bcfont);
                    padding: 0;
                    padding-bottom: 40px;
                }
                /* Themed placeholder color for Quill editor */
                .ql-editor.ql-blank::before {
                    color: var(--bctext-2) !important;
                }
                `}
            </style>
            <div ref={editorRef} className="font-theme w-full h-full flex-1" />
            <ContentBottomGradient />
            </div>
        </NotesErrorBoundary>
    );
}